import React, { CSSProperties, useEffect, useRef, useState } from "react";
import { Button, Dropdown, Form } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faVolumeHigh,
    faVolumeLow,
    faVolumeMedium,
    faVolumeOff,
    faVolumeSlash,
} from "@fortawesome/pro-regular-svg-icons";
import "./volumeControls.scss";
import { useTranslation } from "react-i18next";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import CustomMenu from "../../table/CustomMenu";
import CustomToggle from "../../table/CustomToggle";
import { generateUUID } from "userful-chronos-app-common-js/dist/utils";
import UserfulTooltip from "../../Widgets/UserfulTooltip";
import { UClientComp } from "userful-chronos-app-common-js/dist/models/uclient";
import { REQUEST_UPDATE_UCLIENT_AUDIO } from "userful-chronos-app-common-js/dist/message/messageTypeDefinitions/displays/UClientsMsgDefinations";
import { StringID } from "userful-chronos-app-common-js/dist/models/common";
import { getGlobalStates } from "userful-chronos-app-common-js/dist/globalstates/globalStates";
import { AudioSettings } from "userful-chronos-app-common-js/dist/models/cdm/CdmAudio";

const MAX_VOLUME_LEVEL = 100.0;
const MIN_VOLUME_LEVEL = 0;

interface IProps {
    uclient: UClientComp;
    disabled?: boolean;
    absolutePosition?: { top: number; left: number };
}

const updateAudioSettings = (id: StringID, settings: AudioSettings) => {
    getGlobalStates().eventBus.sendMsg(REQUEST_UPDATE_UCLIENT_AUDIO, { id, settings });
};

export default function UClientVolumeControls({ uclient, disabled, absolutePosition }: IProps) {
    const ref = useRef<HTMLElement>(null);
    const { t } = useTranslation();

    const [audioSettings, setAudioSettings] = useState<AudioSettings>(uclient.audioSettings);
    const [lockUpdate, setLockUpdate] = useState(false);
    const lockRef = useRef(null);

    useEffect(() => {
        if (!lockUpdate) {
            setAudioSettings(uclient.audioSettings);
        }
    }, [uclient]);

    const lock = () => {
        clearTimeout(lockRef.current);
        setLockUpdate(true);
        lockRef.current = setTimeout(() => setLockUpdate(false), 1000);
    };

    const getIcon = function (): IconProp {
        let icon = faVolumeSlash;
        let muted = audioSettings.audioVolume.muted;
        let level = audioSettings.audioVolume.level;

        if (!muted) {
            if (level === 0) icon = faVolumeOff;
            else if (level <= 25) icon = faVolumeLow;
            else if (level <= 75) icon = faVolumeMedium;
            else icon = faVolumeHigh;
        }
        return icon;
    };

    const handleMuteButtonClicked = function (e) {
        lock();
        let muted = !audioSettings.audioVolume.muted;
        let level = audioSettings.audioVolume.level;
        let audioOffset = audioSettings.audioOffset;

        const nAudioSettings = { audioVolume: { muted: muted, level: level }, audioOffset: audioOffset };
        setAudioSettings(nAudioSettings);
        updateAudioSettings(uclient.id, nAudioSettings);
    };

    const handleChange = function (e: any) {
        lock();
        let muted = audioSettings.audioVolume.muted;
        let audioOffset = audioSettings.audioOffset;
        let value = e.target.value;
        const nAudioSettings = { audioVolume: { muted: muted, level: value }, audioOffset: audioOffset };
        setAudioSettings(nAudioSettings);
        updateAudioSettings(uclient.id, nAudioSettings);
    };

    return (
        <div
            className="volumeControlWrapper"
            onMouseDown={(e) => e.stopPropagation()}
            onMouseUp={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
        >
            <div className={`sourceInteractiveViewerLink`} onClick={handleMuteButtonClicked}>
                <div className="iconInside">
                    <FontAwesomeIcon icon={getIcon()} />
                </div>
            </div>
            <Form.Range
                id={"VOL_CTRL_" + generateUUID()}
                defaultValue={audioSettings.audioVolume.level}
                value={audioSettings.audioVolume.level}
                min={MIN_VOLUME_LEVEL}
                max={MAX_VOLUME_LEVEL}
                onChange={handleChange}
                bsPrefix="volume-controls-slider"
                disabled={disabled}
            />
        </div>
    );
}
